import makeMWayTree from "./make-m-way-tree.json";

export class VehicleMakeTree {
  private root: any;

  constructor() {
    this.root = makeMWayTree;
  }

  search(input: string, maxDistance: number): string[] {
    input = input.toLowerCase();
    const results: string[] = [];
    this.searchRecursive(this.root, "", input, maxDistance, results);
    return results;
  }

  private searchRecursive(
    node: any,
    currentMake: string,
    searchMake: string,
    maxDistance: number,
    results: string[],
  ): void {
    if (
      node.isEndOfMake &&
      this.levenshteinDistance(currentMake, searchMake) <= maxDistance
    ) {
      results.push(currentMake);
    }

    for (const char in node) {
      if (char !== "isEndOfMake") {
        this.searchRecursive(
          node[char],
          currentMake + char,
          searchMake,
          maxDistance,
          results,
        );
      }
    }
  }

  public levenshteinDistance(a: string, b: string): number {
    const matrix: number[][] = [];

    for (let i = 0; i <= b.length; i++) {
      matrix[i] = [i];
    }

    for (let j = 0; j <= a.length; j++) {
      matrix[0][j] = j;
    }

    for (let i = 1; i <= b.length; i++) {
      for (let j = 1; j <= a.length; j++) {
        if (b.charAt(i - 1) === a.charAt(j - 1)) {
          matrix[i][j] = matrix[i - 1][j - 1];
        } else {
          matrix[i][j] = Math.min(
            matrix[i - 1][j - 1] + 1,
            matrix[i][j - 1] + 1,
            matrix[i - 1][j] + 1,
          );
        }
      }
    }

    return matrix[b.length][a.length];
  }
}

export class VehicleParser {
  private makeTree: VehicleMakeTree;
  private colors: Set<string>;
  private colorAdjectives: Set<string>;
  private commonColorMappings: { [key: string]: string };
  private commonNameMappings: { [key: string]: string };
  private partialMakeWords: Set<string>;

  constructor() {
    this.makeTree = new VehicleMakeTree();
    this.colors = new Set([
      "aluminum",
      "amethyst",
      "beige",
      "black",
      "blue",
      "blue, dark",
      "blue, light",
      "bronze",
      "brown",
      "burgundy",
      "camouflage",
      "chrome",
      "copper",
      "cream",
      "gold",
      "gray",
      "green",
      "green, dark",
      "green, light",
      "ivory",
      "lavender",
      "maroon",
      "mauve",
      "multicolored",
      "orange",
      "pink",
      "purple",
      "red",
      "silver",
      "tan",
      "taupe",
      "teal",
      "turquoise",
      "white",
      "yellow",
    ]);
    this.colorAdjectives = new Set(["dark", "light"]);
    this.commonColorMappings = {
      grey: "gray",
      navy: "blue, dark",
    };
    this.commonNameMappings = {
      mercedes: "mercedes-benz",
      chevy: "chevrolet",
      vw: "volkswagen",
    };
    this.partialMakeWords = new Set([
      "land",
      "rover",
      "alfa",
      "romeo",
      "aston",
      "martin",
    ]);
  }

  parse(input: string): {
    color: string;
    make: string;
    model: string;
    year: string;
  } {
    const words = input.toLowerCase().split(/\s+/);

    const result: {
      color: string;
      make: string;
      model: string;
      year: string;
    } = {
      color: "",
      make: "",
      model: "",
      year: "",
    };

    // Extract year
    const yearMatch = input.match(/\b(19|20)\d{2}\b/);
    if (yearMatch) {
      result.year = yearMatch[0];
      words.splice(words.indexOf(yearMatch[0]), 1);
    }

    let colorAdjective = "";
    let color = "";
    let bestMakeMatch = "";
    let bestMakeDistance = Infinity;
    const leftOverWords = [...words];
    for (const word of words) {
      if (this.colorAdjectives.has(word)) {
        // check if the word is a color adjective
        colorAdjective = word;
        leftOverWords.splice(leftOverWords.indexOf(word), 1);
      } else if (this.colors.has(word)) {
        // check if the word is a color
        color = word;
        leftOverWords.splice(leftOverWords.indexOf(word), 1);
      } else if (this.commonColorMappings[word]) {
        // check if the word is a common invalid color
        color = this.commonColorMappings[word];
        leftOverWords.splice(leftOverWords.indexOf(word), 1);
      } else if (this.commonNameMappings[word]) {
        // check if the word is a common invalid make
        bestMakeMatch = this.commonNameMappings[word];
        bestMakeDistance = 0;
        leftOverWords.splice(leftOverWords.indexOf(word), 1);
      } else if (this.partialMakeWords.has(word)) {
        // special cases for automakers with spaces in their name
        switch (word) {
          case "land":
          case "rover":
            if (words.includes("rover") && words.includes("land")) {
              bestMakeMatch = "land rover";
              bestMakeDistance = 0;
              leftOverWords.splice(leftOverWords.indexOf("land"), 1);
              leftOverWords.splice(leftOverWords.indexOf("rover"), 1);
            }
            break;
          case "alfa":
          case "romeo":
            if (words.includes("alfa") && words.includes("romero")) {
              bestMakeMatch = "alfa romero";
              bestMakeDistance = 0;
              leftOverWords.splice(leftOverWords.indexOf("alfa"), 1);
              leftOverWords.splice(leftOverWords.indexOf("romero"), 1);
            }
            break;
          case "aston":
          case "martin":
            if (words.includes("aston") && words.includes("martin")) {
              bestMakeMatch = "aston martin";
              bestMakeDistance = 0;
              leftOverWords.splice(leftOverWords.indexOf("aston"), 1);
              leftOverWords.splice(leftOverWords.indexOf("martin"), 1);
            }
            break;
        }
      } else {
        const potentialMake = word;

        const matches = this.makeTree.search(potentialMake, 2);
        if (matches.length > 0) {
          const distance = this.makeTree.levenshteinDistance(
            matches[0],
            potentialMake,
          );
          if (distance < bestMakeDistance) {
            bestMakeDistance = distance;
            bestMakeMatch = matches[0];
            leftOverWords.splice(leftOverWords.indexOf(potentialMake), 1);
          }
        }
      }
    }

    if (
      colorAdjective &&
      color &&
      this.colors.has(`${color}, ${colorAdjective}`)
    ) {
      // check if we have a color and a color adjective and they form a valid color
      result.color = `${color}, ${colorAdjective}`;
    } else if (color) {
      result.color = color;
    }

    // if we have a make, then we can use the make
    if (bestMakeMatch) {
      result.make = bestMakeMatch;
    }

    // Assume remaining words form the model
    if (leftOverWords.length > 0) {
      result.model = leftOverWords.join(" ");
    }

    return result;
  }
}
